<script setup lang="ts">
import BlockRenderer from '@/client/components/BlockRenderer.vue';
import SiteLayout from '@/client/layouts/SiteLayout.vue';
import type { SeoContent, SitePageProps } from '@/client/types';
import { Head } from '@inertiajs/vue3';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<
    SitePageProps & {
        sections: App.Data.SiteBlockData[];
        products: App.Data.SiteProductData[];
        sources: { id: number; name: string }[];
        seo: SeoContent;
        captcha: App.Data.CaptchaWidgetData | null;
    }
>();

const NAV_TYPES: App.Enums.LandingBlockType[] = ['products', 'faq', 'lead_form', 'contacts'];

const { t } = useI18n();

const links = computed(() =>
    props.sections
        .filter((section) => NAV_TYPES.includes(section.type) && (section.type !== 'products' || props.products.length > 0))
        .map((section) => ({ href: `#${section.key}`, label: t(`site.nav_${section.type}`) })),
);

/**
 * Every call to action leads to the request form while its block is shown.
 */
const ctaHref = computed(() => {
    const form = props.sections.find((section) => section.type === 'lead_form');

    return form ? `#${form.key}` : null;
});
</script>

<template>
    <Head :title="seo.meta_title || brandName" />

    <SiteLayout :site="props" :links="links" :cta-href="ctaHref">
        <BlockRenderer
            v-for="section in sections"
            :key="section.key"
            :block="section"
            :cta-href="ctaHref"
            :captcha="captcha"
            :privacy-url="privacyUrl"
            :offer-url="offerUrl"
            :products="products"
            :sources="sources"
        />
    </SiteLayout>
</template>
